import type { GridPoint } from '../../core/types';

/**
 * Flow fields for hordes (D127): one breadth-first distance map per goal
 * tile, shared by every infected walking there. A walker then just steps to
 * the neighbour with the smaller distance — no per-unit A* — which is what lets
 * the simulation carry thousands of them. Deterministic: fixed neighbour order.
 */
const NEIGHBOURS: readonly (readonly [number, number])[] = [
  [1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [-1, 1], [1, -1], [-1, -1],
];

export class FlowFields {
  private readonly cache = new Map<number, Int32Array>();
  private version = 0;
  private builtAt = new Map<number, number>();
  /** Fields built (for tests / profiling). */
  built = 0;

  constructor(
    private readonly width: number,
    private readonly height: number,
  ) {}

  /** Call when walls / buildings change: fields rebuild lazily on next use. */
  invalidate(): void {
    this.version++;
  }

  /** Distance map to `goal` (−1 = cannot reach). `passable` is the infected's passability. */
  field(goal: GridPoint, passable: (x: number, y: number) => boolean): Int32Array {
    const key = goal.y * this.width + goal.x;
    const have = this.cache.get(key);
    if (have && this.builtAt.get(key) === this.version) return have;
    const w = this.width;
    const h = this.height;
    const dist = new Int32Array(w * h).fill(-1);
    const queue = new Int32Array(w * h);
    let head = 0;
    let tail = 0;
    // The goal itself may be a building tile (a Command Center): seed from it and its walkable ring.
    dist[key] = 0;
    queue[tail++] = key;
    while (head < tail) {
      const k = queue[head++]!;
      const x = k % w;
      const y = (k - x) / w;
      const d = dist[k]!;
      for (const [dx, dy] of NEIGHBOURS) {
        const nx = x + dx;
        const ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
        const nk = ny * w + nx;
        if (dist[nk] !== -1 || !passable(nx, ny)) continue;
        // No corner cutting between two blocked tiles.
        if (dx !== 0 && dy !== 0 && !passable(x + dx, y) && !passable(x, y + dy)) continue;
        dist[nk] = d + 1;
        queue[tail++] = nk;
      }
    }
    if (this.cache.size > 24) this.cache.clear();
    this.cache.set(key, dist);
    this.builtAt.set(key, this.version);
    this.built++;
    return dist;
  }

  /**
   * Up to `steps` waypoints from `start` downhill towards the goal, or null when
   * `start` is not connected to it (the caller falls back to A*).
   */
  route(start: GridPoint, goal: GridPoint, passable: (x: number, y: number) => boolean, steps: number): GridPoint[] | null {
    const dist = this.field(goal, passable);
    const w = this.width;
    let k = start.y * w + start.x;
    if (start.x < 0 || start.y < 0 || start.x >= w || start.y >= this.height || dist[k]! < 0) return null;
    const out: GridPoint[] = [];
    let x = start.x;
    let y = start.y;
    for (let i = 0; i < steps && dist[k]! > 0; i++) {
      let best = -1;
      let bestD = dist[k]!;
      for (const [dx, dy] of NEIGHBOURS) {
        const nx = x + dx;
        const ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= w || ny >= this.height) continue;
        const nk = ny * w + nx;
        const nd = dist[nk]!;
        if (nd >= 0 && nd < bestD) {
          bestD = nd;
          best = nk;
        }
      }
      if (best < 0) break;
      k = best;
      x = k % w;
      y = (k - x) / w;
      if (dist[k] === 0 && !passable(x, y)) break; // the goal tile is the building itself: stop beside it
      out.push({ x, y });
    }
    return out;
  }
}
